import * as React from 'react';
import { useStaticQuery, Link, graphql } from 'gatsby';
import { library, icon } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLinkedinIn,
  faFacebookF,
  faInstagram,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { useLocation, navigate } from '@reach/router';
import clsx from 'clsx';
import * as icons from '../icons/icon-pack';
import useWindowSize from '../hooks/useWindowSize';
import { extractLanguage } from '../helpers/common';
import { useSlug } from '../contexts/SlugContext';

function pathnameMatchesSlug(pathname, fullSlug) {
  let slug = fullSlug;
  if (slug.startsWith('/')) {
    slug = slug.replace('/', '');
  }

  if (slug.slice(-1) === '/') {
    slug = slug.substring(0, slug.length - 1);
  }

  return pathname.includes(slug) || slug.includes(pathname);
}

function Header({ setIsNavOpen }) {
  library.add(faLinkedinIn, faFacebookF, faInstagram, faLocationDot);

  const handleLanguageSwitcherClick = () => {
    setIsNavOpen(false);
    document.body.classList.remove('lock-scroll');
  }

  const { header } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            footer {
              menuLinks {
                title
                links {
                  name
                  link
                  icon
                }
              }
              additionalText
            }
          }
        }
        header: allStoryblokEntry(filter: { name: { eq: "Menu" } }) {
          edges {
            node {
              name
              slug
              lang
              content
              field_component
              first_published_at
              full_slug
              uuid
            }
          }
          pageInfo {
            currentPage
            pageCount
          }
        }
      }
    `,
  );

  const windowSize = useWindowSize();

  // initiate isNavOpen state with false
  const handleNavToggle = () => {
    if (windowSize.width >= 1473) return;
    window.__setBodyScrollLock(false);
  };

  const handleNavigation = (to) => {
    handleNavToggle(); // Close the menu
    setTimeout(() => {
      navigate(to); // Navigate after menu animation
      setIsNavOpen(false);
    }, 400);
  };

  const location = useLocation();

  const currentLanguage = extractLanguage(location.pathname);

  library.add({ ...icons });

  const { alternateSlug } = useSlug();

  return (
    <>
      {/* <Script src="https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=UjZT3H" strategy="idle" /> */}
      <div className="w-full m-auto">
        <ul className="flex flex-col items-end lg:items-start gap-4 px-4 py-4 leading-10 lg:text-left lg:mt-8 lg:gap-8 lg:w-fit lg:px-6 lg:py-12">
          {header.edges.map((node) => {
            const menus = JSON.parse(node.node.content);

            return menus.Menu.filter(
              (menu) => menu.lang === currentLanguage,
            ).map((menu) => {
              let prefix;
              let iconName;

              if (menu.content?.Icon) {
                iconName = menu.content?.Icon[0]?.content?.icon_title;
                prefix = menu.content?.Icon[0]?.content?.prefix;
              }

              const readyIcon = icon({ prefix, iconName });
              const isActiveMenuItem = pathnameMatchesSlug(location.pathname, menu.full_slug);
              const isContactMenuItem = menu.full_slug.includes('contact');

              return (
                <React.Fragment key={menu.name}>
                  <li className="relative flex">
                    <Link
                      className={clsx(
                        'relative text-lg lg:text-xl flex items-end lg:items-start',
                        'menu-item-before',
                        'before:absolute before:top-[-0.5rem] before:h-[calc(100%+1rem)] before:z-0 transition-[width] duration-300',
                        'before:left-auto before:lg:left-[-4rem] before:right-[-4rem] lg:before:right-auto',
                        'before:rounded-l-3xl before:lg:rounded-l-none before:lg:rounded-r-3xl',
                        {
                          active: isActiveMenuItem || isContactMenuItem,
                          inactive: !isActiveMenuItem && !isContactMenuItem,
                          contact: isContactMenuItem,
                        },
                      )}
                      to={`/${menu.full_slug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation(`/${menu.full_slug}`);
                      }}
                      title={menu.full_slug}
                    >
                      <div className="relative flex flex-row items-center justify-end gap-4">
                        <div className="flex-shrink-0 w-[32px] flex justify-start">
                          <FontAwesomeIcon
                            icon={readyIcon}
                            size="1x"
                            className="w-[32px] h-[32px]"
                          />
                        </div>
                        <div className="flex w-full menu-label xll:hidden -order-1 xll:order-2 whitespace-nowrap">
                          {menu.content?.Name || menu.name}
                        </div>
                      </div>
                    </Link>
                  </li>
                </React.Fragment>
              );
            });
          })}
        </ul>
        {header?.edges.map(({ node }, i) => {
          const content = JSON.parse(node.content);
          const cleanPathname = location.pathname.replace(/^\/[a-z]{2}(?:\/(.*))?$/i, '/$1').replace(/\/$/, '') || '/';
          if (i === 0 && content?.language_switcher) {
            return (
              <div
                key={node.name}
                className="relative flex flex-row-reverse items-center w-full gap-4 px-4 py-4 leading-10 language-switcher flex-start lg:flex-row lg:text-left lg:gap-4 lg:w-fit lg:px-6  lg:justify-end xll:justify-start"
              >
                <div className="flex-shrink-0 w-[24px] flex justify-center">
                  <FontAwesomeIcon icon={faGlobe} size="1x" />
                </div>
                <div className="flex flex-row gap-2">
                  <Link
                    to={`${alternateSlug?.en?.startsWith('/') ? alternateSlug.en : `/${alternateSlug.en}`}`}
                    className={`language-link ${
                      currentLanguage === 'en' ? 'active' : ''
                    }`}
                    onClick={handleLanguageSwitcherClick}
                  >
                    EN
                  </Link>
                  <span>|</span>
                  <Link
                    to={`${alternateSlug?.de?.startsWith('/') ? alternateSlug.de : `/${alternateSlug.de}`}`}
                    className={`language-link ${
                      currentLanguage === 'default' ? 'active' : ''
                    }`}
                    onClick={handleLanguageSwitcherClick}
                  >
                    DE
                  </Link>
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    </>
  );
}

export default Header;
